import { useMsal } from '@azure/msal-react';
import { B2CCustomPolicyRoutes } from '@copilot/data/config/routes';
import { isNil } from 'lodash';
import { getAuthRedirectRequestOptions } from '@copilot/common/utils/authenticationProvider';

type ImpersonationFunction = (
    userId: string | undefined,
    orgId: string | undefined,
    path?: string
) => void;

/**
 * Hook to handle impersonation logic
 */
export function useImpersonation(): [ImpersonationFunction, () => void] {
    const { instance } = useMsal();

    const onImpersonation = (
        userId: string | undefined,
        orgId: string | undefined,
        path = '/'
    ): void => {
        if (isNil(userId) || isNil(orgId)) return;

        const authOptions = getAuthRedirectRequestOptions({
            authority: B2CCustomPolicyRoutes.Impersonation,
            scopes: [],
            extraQueryParameters: {
                orgId,
                userId,
            },
            redirectStartPage: path,
        });

        // Use the MSAL instance directly to avoid automatic token management
        instance.loginRedirect(authOptions);
    };

    const onExitImpersonation = (): void => {
        window.close();
    };

    return [
        (...params) => {
            onImpersonation(...params);
        },
        (...params) => onExitImpersonation(...params),
    ];
}

/**
 * Handle redirect to impersonation in new tab
 */
export function useImpersonationWithNewTab(): ImpersonationFunction {
    return function (userId: string | undefined, orgId: string | undefined, path = '/') {
        if (isNil(userId) || isNil(orgId)) return;
        window.mixpanel?.track('impersonate', { userId, orgId, path });
        const userOrgPath = `${userId}/${orgId}`;
        window.open(`/impersonate/${userOrgPath}?path=${encodeURIComponent(path)}`, userOrgPath);
    };
}
